// src/components/CreateSpace/Step2/hooks/useFormValidation.js

import { useCallback, useState } from "react";
import { getDispositionTypes } from "helpers/getSpaceDispositionTypes";
import { ALLOWED_IMAGE_TYPES } from "../step2.constants"; // Import this

export const useFormValidation = (formValues, t) => {
  const [fieldErrors, setFieldErrors] = useState({});
  
  // Safe accessor for placeUsage key
  const getPlaceUsageKey = useCallback(() => {
    try {
      return formValues?.placeUsage?.key || null;
    } catch (error) {
      console.warn('Error accessing placeUsage key:', error);
      return null;
    }
  }, [formValues]);
  
  const requiresWorkPlaces = useCallback((placeUsageKey) => {
    if (!placeUsageKey || typeof placeUsageKey !== 'string') return false;
    return ["private_office", "shared_office", "coworking"].includes(placeUsageKey);
  }, []);
  
  const requiresDispositionTypes = useCallback((placeUsageKey) => {
    if (!placeUsageKey || typeof placeUsageKey !== 'string') return false;
    return ["seminar", "teambuilding", "meeting_room", "conference_room"].includes(placeUsageKey);
  }, []);
  
  const requiresSeminarSection = useCallback((placeUsageKey) => {
    if (!placeUsageKey || typeof placeUsageKey !== 'string') return false;
    return ["seminar", "teambuilding"].includes(placeUsageKey);
  }, []);
  
  // NEW: Validate image type
  const validateImageType = useCallback((file) => {
    if (!(file instanceof File)) return true; // Not a file, skip type validation
    
    const fileName = file.name || '';
    const extension = fileName.split('.').pop().toLowerCase();
    const validExtensions = ['jpg', 'jpeg'];
    
    const hasValidMimeType = ALLOWED_IMAGE_TYPES.includes(file.type);
    const hasValidExtension = validExtensions.includes(extension);
    
    // Special case: text/plain might be a misidentified JPG
    if (file.type === 'text/plain' && hasValidExtension) {
      return true; // Allow but will warn in compression
    }
    
    return hasValidMimeType || hasValidExtension;
  }, []);
  
  // Updated to handle file type validation
  const isValidImageField = useCallback((value) => {
    try {
      // Check if it's a valid File object
      if (value instanceof File) {
        // Validate file type
        return validateImageType(value);
      }
      // Check if it's a string (existing image URL)
      return (typeof value === 'string' && value && value.length > 0 && value !== "null");
    } catch (error) {
      return false;
    }
  }, [validateImageType]);
  
  const validateField = useCallback((fieldName, value) => {
    const errors = {};
    const placeUsageKey = getPlaceUsageKey();
    
    try {
      // Handle image fields specially
      if (fieldName && fieldName.startsWith('image_')) {
        // For image_1 (required)
        if (fieldName === 'image_1') {
          if (!value) {
            errors[fieldName] = t('validation.required') || 'Cover image is required.';
          } else if (!isValidImageField(value)) {
            errors[fieldName] = t('validation.invalidImageType', { types: 'JPG, JPEG, PNG' }) || 
                                'Invalid image format. Only JPG/JPEG/PNG allowed.';
          }
        } 
        // For other images (optional, but validate type if present)
        else if (value && !isValidImageField(value)) {
          errors[fieldName] = t('validation.invalidImageType', { types: 'JPG, JPEG' }) || 
                              'Invalid image format. Only JPG/JPEG allowed.';
        }
        return errors;
      }
      
      // Handle other fields
      switch (fieldName) {
        case 'area':
          if (value === null || value === undefined || value === '') {
            errors[fieldName] = t('validation.required') || 'Area is required.';
          } else {
            const numValue = Number(value);
            if (isNaN(numValue) || numValue <= 0) {
              errors[fieldName] = t('validation.areaPositive') || 'Area must be a positive number.';
            }
          }
          break;
          
        case 'desk':
          if (requiresWorkPlaces(placeUsageKey)) {
            if (value === null || value === undefined || value === '') {
              errors[fieldName] = t('validation.required') || 'Number of work places is required.';
            } else {
              const numValue = Number(value);
              if (isNaN(numValue) || numValue < 0) {
                errors[fieldName] = t('validation.deskPositive') || 'Number of work places must be a positive number.';
              }
            }
          }
          break;
          
        default:
          // Validate numeric fields for disposition types
          if (fieldName && typeof fieldName === 'string' && 
              (fieldName.includes('disposition_') || fieldName.includes('_type_'))) {
            if (value !== null && value !== undefined && value !== '') {
              const numValue = Number(value);
              if (isNaN(numValue) || numValue < 0) {
                errors[fieldName] = t('validation.positiveNumber') || 'Must be a positive number.';
              }
            }
          }
          break;
      }
    } catch (error) {
      console.warn(`Error validating field ${fieldName}:`, error);
      errors[fieldName] = t('validation.fieldError') || 'Invalid field value.';
    }
    
    return errors;
  }, [getPlaceUsageKey, requiresWorkPlaces, isValidImageField, t]);
  
  const validateSeminars = useCallback(() => {
    const errors = {};
    const placeUsageKey = getPlaceUsageKey();
    
    try {
      if (requiresSeminarSection(placeUsageKey)) {
        const seminars = Array.isArray(formValues?.seminars) ? formValues.seminars : [];
        
        const hasAtLeastOneOffer = seminars.some(
          seminar => seminar && typeof seminar === 'object' && 
                    seminar.offer && typeof seminar.offer === 'string' && 
                    seminar.offer.trim() !== ''
        );
        
        if (!hasAtLeastOneOffer) {
          errors.seminars = t('validation.atLeastOneSeminarOffer') || 
                            'You must choose at least one offer.';
        }
      }
    } catch (error) {
      console.warn('Error validating seminars:', error);
      errors.seminars = t('validation.seminarError') || 'Error validating seminar section.';
    }
    
    return errors;
  }, [formValues, getPlaceUsageKey, requiresSeminarSection, t]);
  
  const validateDispositionTypes = useCallback(() => {
    const errors = {};
    const placeUsageKey = getPlaceUsageKey();
    
    try {
      if (requiresDispositionTypes(placeUsageKey)) {
        let dispositionTypes = [];
        try {
          dispositionTypes = getDispositionTypes(t) || [];
        } catch (error) {
          console.warn('Error getting disposition types:', error);
        }
        
        if (!Array.isArray(dispositionTypes) || dispositionTypes.length === 0) {
          return errors;
        }
        
        let hasAtLeastOnePositiveValue = false;
        const dispositionErrors = {};
        
        dispositionTypes.forEach(type => {
          if (type && typeof type === 'object' && type.key2) {
            const value = formValues?.[type.key2];
            
            if (value !== null && value !== undefined && value !== '') {
              const numValue = Number(value);
              if (!isNaN(numValue) && numValue > 0) {
                hasAtLeastOnePositiveValue = true;
              }
            }
            
            if (value !== null && value !== undefined && value !== '') {
              const numValue = Number(value);
              if (isNaN(numValue) || numValue < 0) {
                dispositionErrors[type.key2] = t('validation.positiveNumber') || 'Must be a positive number.';
              }
            }
          }
        });
        
        if (!hasAtLeastOnePositiveValue) {
          errors.dispositionTypes = t('validation.atLeastOneDispositionType') || 
                                    'You must specify at least one disposition type with a positive number.';
        }
        
        Object.assign(errors, dispositionErrors);
      }
    } catch (error) {
      console.warn('Error validating disposition types:', error);
      errors.dispositionTypes = t('validation.dispositionError') || 'Error validating disposition types.';
    }
    
    return errors;
  }, [formValues, getPlaceUsageKey, requiresDispositionTypes, t]);
  
  const validateAllRequiredFields = useCallback(() => {
    const errors = {};
    const placeUsageKey = getPlaceUsageKey();
    
    try {
      // Validate area
      const areaErrors = validateField('area', formValues?.area);
      Object.assign(errors, areaErrors);
      
      // Validate desk if required
      if (requiresWorkPlaces(placeUsageKey)) {
        const deskErrors = validateField('desk', formValues?.desk);
        Object.assign(errors, deskErrors);
      }
      
      // Validate ALL images (not just cover)
      for (let i = 1; i <= 6; i++) {
        const imageKey = `image_${i}`;
        const imageErrors = validateField(imageKey, formValues?.[imageKey]);
        Object.assign(errors, imageErrors);
      }
      
      // Validate disposition types if required
      if (requiresDispositionTypes(placeUsageKey)) {
        const dispositionErrors = validateDispositionTypes();
        Object.assign(errors, dispositionErrors);
      }
      
      // Validate seminars if required
      if (requiresSeminarSection(placeUsageKey)) {
        const seminarErrors = validateSeminars();
        Object.assign(errors, seminarErrors);
      }
      
    } catch (error) {
      console.error('Error in validateAllRequiredFields:', error);
      errors.general = t('validation.generalError') || 'Validation error occurred.';
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formValues, validateField, validateSeminars, validateDispositionTypes, 
      getPlaceUsageKey, requiresWorkPlaces, requiresDispositionTypes, 
      requiresSeminarSection, t]);
  
  const clearFieldError = useCallback((fieldName) => {
    if (!fieldName || typeof fieldName !== 'string') return;
    
    setFieldErrors(prev => {
      try {
        const newErrors = { ...(prev || {}) };
        
        if (fieldName.includes('disposition_') || fieldName.includes('_type_')) {
          const placeUsageKey = getPlaceUsageKey();
          if (requiresDispositionTypes(placeUsageKey)) {
            let dispositionTypes = [];
            try {
              dispositionTypes = getDispositionTypes(t) || [];
            } catch (error) {
              console.warn('Error getting disposition types:', error);
            }
            
            let hasPositiveValue = false;
            if (Array.isArray(dispositionTypes)) {
              dispositionTypes.forEach(type => {
                if (type && typeof type === 'object' && type.key2) {
                  const value = formValues?.[type.key2];
                  if (value !== null && value !== undefined && value !== '') {
                    const numValue = Number(value);
                    if (!isNaN(numValue) && numValue > 0) {
                      hasPositiveValue = true;
                    }
                  }
                }
              });
            }
            
            if (hasPositiveValue) {
              delete newErrors.dispositionTypes;
            }
          }
        }
        
        delete newErrors[fieldName];
        return newErrors;
      } catch (error) {
        console.warn('Error clearing field error:', error);
        return prev || {};
      }
    });
  }, [formValues, getPlaceUsageKey, requiresDispositionTypes, t]);
  
  const clearAllErrors = useCallback(() => {
    setFieldErrors({});
  }, []);
  
  return {
    fieldErrors: fieldErrors || {},
    validateAllRequiredFields,
    validateField,
    clearFieldError,
    clearAllErrors,
    requiresWorkPlaces,
    requiresDispositionTypes,
    requiresSeminarSection
  };
};